import type { TKey } from "./en";
import { ruAdmin } from "./ru.admin";
import { ruShell } from "./ru.shell";
import { ruCampaigns } from "./ru.campaigns";
import { ruCard } from "./ru.card";
import { ruReports } from "./ru.reports";
import { ruEditor } from "./ru.editor";
import { ruOffers } from "./ru.offers";
import { ruOps } from "./ru.ops";
import { ruLanders } from "./ru.landers";
import { ruAdaptive } from "./ru.adaptive";
import { ruPlan } from "./ru.plan";

/**
 * Russian — the language the operators actually work in.
 *
 * Typed as `Record<TKey, string>` rather than inferred: that is the whole
 * point of the layer. Add a key to `en.ts` and forget it here and the build
 * fails, instead of a dotted key leaking onto the screen in production.
 *
 * Hints answer "зачем это поле", not "что это за поле" — an operator who
 * already reads the label does not need it repeated.
 */
export const ru: Record<TKey, string> = {
  ...ruAdmin,
  ...ruShell,
  ...ruCampaigns,
  ...ruCard,
  ...ruReports,
  ...ruEditor,
  ...ruOffers,
  ...ruOps,
  ...ruLanders,
  ...ruAdaptive,
  ...ruPlan,
  // — шапка —
  "campaign.back": "Кампании",
  "campaign.action.pause": "Пауза",
  "campaign.action.activate": "Запустить",
  "campaign.action.more": "Ещё",
  "campaign.action.duplicate": "Дублировать",
  "campaign.action.archive": "В архив",
  "campaign.notFound": "Кампания не найдена — возможно, её отправили в архив.",
  "campaign.toast.paused": "«{name}» на паузе",
  "campaign.toast.activated": "«{name}» запущена",
  "campaign.toast.failed": "Не получилось",
  "campaign.archive.title": "Отправить «{name}» в архив?",
  "campaign.archive.body": "Кампания перестанет принимать трафик; вернуть её можно через фильтр «в архиве».",
  "campaign.archive.done": "«{name}» в архиве",

  // — вкладки —

  // — статистика: судьба конверсий —

  // — ленды: какой работает, а какой тихо съедает бюджет —

  // — настройки —
  // Подпись самого поля сюда не пишется: её объявляет площадка
  // (internal/sourcetype, AskField.Label), и у каждой она своя — у Meta
  // «пиксель», у Google вообще «ID конверсии». Здесь только общая
  // подсказка с примером из самой же спецификации.
  "branch.trigger.back": "Кнопка «Назад»",
  "branch.trigger.back.hint": "Человек нажал «Назад» в браузере.",
  "branch.trigger.exit_intent": "Попытка уйти",
  "branch.trigger.exit_intent.hint": "Курсор ушёл за край окна, а на телефоне — резкая прокрутка страницы вверх.",
  "branch.trigger.idle": "Бездействие",
  "branch.trigger.idle.hint": "Ни касаний, ни прокрутки, ни нажатий столько секунд, сколько вы задали.",
  "branch.trigger.scroll_end": "Дочитал до конца",
  "branch.trigger.scroll_end.hint": "Человек долистал страницу до самого низа.",
  "branch.trigger.next_step": "Второй шаг",
  "branch.trigger.next_step.hint": "Кнопка первой страницы ведёт на лендинги второго шага.",

  // — рекламная ссылка: что заполняем и что копируем —

  // — список кампаний: экран, который медиабайер открывает каждое утро —

  // — шапки-полосы над колонками —
  "campaigns.band.traffic": "Трафик",
  "campaigns.band.conversions": "Конверсии",
  "campaigns.band.money": "Деньги",



  // — админка: шаблоны рекламных ссылок —
  "adlink.title": "Шаблоны рекламных ссылок",
  "adlink.subtitle":
    "Что каждая площадка спрашивает у байера и что он копирует обратно. Правится в internal/sourcetype — экран читает тот же сгенерированный реестр, что и диалог.",
  "adlink.platform": "Площадка",
  "adlink.ask": "Что спрашиваем",
  "adlink.ask.empty": "У этого источника нет рекламного кабинета — спрашивать и отдавать нечего.",
  "adlink.ask.key": "Ключ",
  "adlink.ask.label": "Подпись (по-русски)",
  "adlink.ask.rule": "Правило",
  "adlink.ask.required": "Обязательное",
  "adlink.ask.error": "Сообщение при ошибке",
  "adlink.give": "Что байер копирует",
  "adlink.give.label": "Подпись",
  "adlink.give.adField": "Поле в рекламном кабинете",
  "adlink.give.value": "Значение",
  "adlink.rules": "Правила площадок и ошибки, которые они ловят",
  "adlink.rules.example": "Как выглядит",
  "adlink.rules.mistake": "Что вставляют вместо",
  "adlink.yes": "да",
  "adlink.no": "нет",
  "adlink.source":
    "Эти строки — данные, а не текст интерфейса: площадка переименовывает поле или удлиняет ID куда чаще, чем меняется этот экран. Правьте internal/sourcetype/specs_*.go и пересоберите: UPDATE_SOURCETYPE_SNAPSHOT=1 go test ./internal/sourcetype/...",
  // — навигация оболочки —
  "nav.today": "Сегодня",
  "nav.campaigns": "Кампании",
  "nav.journal": "Журнал",
  "nav.domains": "Домены",
  "nav.settings": "Настройки",
  "nav.newCampaign": "Новая кампания",
  "nav.admin": "Админка",
  "nav.search": "Кампания, клик, оффер, ленд",
  "nav.signOut": "Выйти",
  "lib.title": "Библиотека",
  "journal.title": "Журнал",
  "journal.tab.clicks": "Клики",
  "journal.tab.conversions": "Конверсии",
  "journal.tab.bots": "Боты",
  "settings.title": "Настройки",
  "settings.tab.workspace": "Общие",
  "settings.tab.goals": "Цели",
  "settings.tab.connections": "Подключения",
  "admin.title": "Админка",
  "admin.subtitle": "Настройки платформы: они действуют на все рабочие пространства, а не только на ваше.",
  "admin.tab.flows": "Потоки",
  "admin.tab.flowTemplates": "Шаблоны потоков",
  "admin.tab.tagManager": "Тег-менеджер",
  "admin.tab.fraudWeights": "Веса антифрода",
  "admin.tab.ipIntel": "IP-данные",
  "admin.tab.edge": "Край",
  "admin.tab.adLinks": "Шаблоны рекламных ссылок",

  // — «Сегодня»: домашний экран, который байер открывает каждое утро —
  "today.error": "Не удалось загрузить сегодняшние цифры",

  // — первый день: ни одной кампании ещё не создано —
  "today.checklist.network": "Партнёрка",
  "today.checklist.offer": "Оффер",
  "today.checklist.lander": "Ленд",

  // — денежная строка —

  // — сегодняшние кампании, по профиту —

  // — график —

  // — непросмотренные срабатывания правил —
  // — домены для ссылок: байер подключает свой домен сам —
  // Один сплошной блок в конце словаря: параллельная ветка переносит маршрут
  // этого экрана, и конфликт здесь должен решаться одним куском.
  "domains.action.checking": "Проверяем…",
  "domains.search.label": "Поиск по доменам",
  "domains.col.domain": "Домен",
  "domains.col.state": "Состояние",
  "domains.badge.default": "по умолчанию",
  "domains.default.make": "Использовать для новых кампаний",
  "domains.default.done": "{fqdn} теперь подставляется в новые кампании",
  "domains.default.failed": "Не получилось сменить домен по умолчанию",

  "domains.state.working": "Работает",
  "domains.state.waiting": "Ждём DNS",
  "domains.state.failed": "Ошибка",
  "domains.state.disabled": "Отключён",
  "domains.state.unknown": "Ещё не проверяли",
  "domains.state.working.hint": "Домен отвечает, кампании на нём можно запускать.",
  "domains.state.waiting.hint":
    "Запись ещё не разошлась по интернету. У регистраторов это обычно 5–30 минут, иногда пара часов.",
  "domains.state.failed.hint": "Домен подключить не удалось. Причина ниже — исправьте и нажмите «Проверить».",
  "domains.state.disabled.hint": "Вы выключили домен. Настройки сохранены, но клики он не принимает.",
  "domains.state.unknown.hint": "Домен ещё ни разу не проверяли. Нажмите «Проверить».",

  "domains.blocked.body":
    "Пока состояние не «Работает», кнопка «Запустить» у кампании с этим доменом вернёт ошибку. С самой кампанией при этом всё в порядке — просто адрес пока не отвечает.",

  "domains.empty.title": "Домена пока нет",
  "domains.empty.body":
    "Домен — это адрес, на котором живёт ваша рекламная ссылка: реклама ведёт человека на ваш адрес, трекер записывает клик и только потом отправляет его на оффер. Купите домен у любого регистратора — или возьмите поддомен того, что уже есть, — и подключите его здесь.",
  "domains.empty.action": "Подключить домен",

  "domains.create.title": "Подключить домен",
  "domains.create.description": "Адрес, на котором будет жить рекламная ссылка.",
  "domains.port.label": "Как подключится домен",
  "domains.port.native.summary": "В один клик — DNS-запись поставят за вас",
  "domains.port.native.register": "Адрес, которого ещё нет в «{adapter}», добавится туда сам.",
  "domains.port.native.done": "{fqdn} подключён. Заработает, как только запись разойдётся — обычно за несколько минут.",
  "domains.port.free.title": "Ваши свободные домены в «{adapter}»",
  "domains.port.free.take": "Подключить",
  "domains.port.free.none": "Свободных доменов в «{adapter}» нет — впишите адрес ниже или купите новый.",
  "domains.port.other": "Другой адрес",
  "domains.port.buy": "Купить новый в «{adapter}»",
  "domains.port.manual.records": "Что поставить в DNS домена",
  "domains.port.manual.steps":
    "Подключите домен — он появится со статусом «Ждём DNS». Поставьте запись у своего DNS-провайдера ровно как написано. Трекер проверяет её каждую минуту, а «Проверить сейчас» в карточке домена — сразу.",
  "domains.port.manual.limit.title": "Когда пойдут клики",
  "domains.port.manual.limit.body":
    "Запись пройдёт проверку у любого DNS-провайдера, но клики дойдут до трекера, только если зона домена сама ведёт трафик на край трекера (ваш Cloudflare с Worker трекера). Домен у другого DNS-провайдера пока клики принимать не будет — подключённый источник доменов поставит всё сам.",
  "domains.port.failed.noAnswer": "«{adapter}» не ответил",
  "domains.port.failed.notWired": "«{adapter}» ещё не настроен для этого трекера — мы уже чиним",
  "domains.port.failed.connector": "Свой сервис доменов подключится, когда появятся коннекторы",
  "domains.port.failed.change": "Изменить в «Подключениях»",
  "domains.create.fqdn": "Адрес домена",
  "domains.create.fqdn.placeholder": "go.example.com",
  "domains.create.fqdn.hint":
    "Лучше поддомен, чем сам домен: если у вас example.com — впишите go.example.com. Сайт на example.com продолжит работать.",
  "domains.create.default": "Использовать этот домен для новых кампаний",
  "domains.create.submit": "Подключить",
  "domains.create.done": "{fqdn} добавлен. Теперь добавьте DNS-запись ниже.",
  "domains.create.failed": "Не получилось добавить домен",

  "domains.detail.checkedNever": "Ещё не проверяли",

  "domains.steps.2": "Добавьте запись из блока ниже ровно как написано. Значения копируйте, а не перепечатывайте.",

  "domains.record.none": "Этому провайдеру от вас ничего не нужно — запись он ставит сам.",


  "domains.check.bound": "{fqdn} работает — на нём можно запускать кампании",
  "domains.check.waiting": "Пока нет: {reason}",
  "domains.check.waitingPlain": "Запись ещё не разошлась по интернету. Подождите десять минут и нажмите «Проверить» снова.",
  "domains.check.failed": "Проверка не прошла",

  "domains.serve.label": "Принимать клики",
  "domains.serve.hint": "Если выключить, домен сохранит настройки, но перестанет принимать клики.",
  "domains.serve.failed": "Не получилось переключить домен",

  "domains.delete.done": "Домен удалён",
  "domains.delete.failed": "Не получилось удалить домен",

  "domains.loadFailed": "Не удалось загрузить домены",
  "domains.unavailable.title": "Сейчас домены подключить нельзя",
  "domains.unavailable.body":
    "На этой установке не настроен сервис доменов, поэтому трекер не может подключить домен. Напишите администратору — с вашей стороны здесь ничего не сделать.",

  // — общая хромка таблиц —
  "table.csv": "Выгрузить CSV",
  "table.selectAll": "Выбрать все",
  "table.selectRow": "Выбрать строку",
  "table.empty": "Строк нет",
  "table.prev": "Назад",
  "table.next": "Вперёд",
  "table.perPage": "на странице",
  "table.summary": "{from}–{to} из {total}",
  "table.page": "Страница {page} из {total}",

  /* ── Библиотека: офферы, ленды, источники, партнёрки ────────────────────
   *
   * Один сплошной блок в конце файла — чтобы ветки, которые переводят
   * соседние экраны, сходились в мерже, о котором не надо думать.
   * Формулировки — для медиабайера: «выплата», а не «payout model».
   */

  "tagPicker.create": "Создать тег",
  "lib.common.retry": "Повторить",
  "lib.common.failed": "Что-то пошло не так",
  "lib.common.edit": "Изменить",
  "lib.common.save": "Сохранить",
  "lib.common.saving": "Сохраняем…",
  "lib.common.create": "Создать",
  "lib.common.cancel": "Отмена",
  "lib.common.copy": "Копировать",
  "lib.common.copied": "{what} — скопировано",
  "lib.common.archive": "В архив",
  "lib.common.duplicate": "Копия",
  "lib.common.refresh": "Обновить",
  "lib.common.loading": "Загружаем…",
  "lib.common.saveFailed": "Не удалось сохранить",
  "lib.common.archiveFailed": "Не удалось убрать в архив",
  "lib.common.optional": "(необязательно)",
  "lib.common.notSet": "Не задано.",
  "lib.common.col.name": "Название",
  "lib.common.col.status": "Статус",
  "lib.common.col.actions": "Действия",
  "lib.common.total": "всего: {count}",

  "lib.status.active": "Работает",
  "lib.status.paused": "На паузе",
  "lib.status.archived": "В архиве",
  "lib.status.draft": "Черновик",






  "lib.flowMode.single": "Один оффер",
  "lib.flowMode.split": "Сплит-тест",
  "lib.flowMode.weighted": "Ротация по весам",
  "lib.flowMode.rules": "По правилам",
  "lib.flowMode.template": "Из шаблона",

  "lib.landers.archivedOne": "«{name}» — в архиве",




  "lib.sources.title": "Источники",
  "lib.sources.new": "Новый источник",
  "lib.sources.newFirst": "Завести первый источник",
  "lib.sources.loadFailed": "Не удалось загрузить источники",
  "lib.sources.empty.title": "Источников пока нет",
  "lib.sources.empty.body": "Выберите площадку — Facebook, TikTok, Google — и трекер сам подставит её метки и рекламную ссылку.",

  "lib.sources.saveFailed": "Не удалось сохранить источник",




  // — Статистика (/stats + /stats/geo) —
  // Один непрерывный блок; ключи с префиксом `stats.` — ровно те же, что в
  // `en.ts`, иначе сборка падает.








  // — Т5: shell, period, campaigns list —
  "common.apply": "Применить",
  "common.cancel": "Отмена",
  "common.undo": "Отменить",
  "common.undoFailed": "Не удалось вернуть",
  "common.deleteFailed": "Не удалось удалить",
  "common.done": "Готово",
  "common.soon": "Скоро",
  "word.campaigns": "кампания|кампании|кампаний",
  "word.groups": "группа|группы|групп",
  "nav.sections": "Разделы",
  "nav.reports": "Отчёты",
  "nav.health": "Здоровье данных",
  "nav.role.admin": "Админ",
  "nav.role.viewer": "Наблюдатель",
  "nav.role.buyer": "Баер",
  "scope.label": "Чьи данные",
  "scope.mine": "Мои",
  "scope.all": "Все",
  "period.label": "Период",
  "period.today": "Сегодня",
  "period.yesterday": "Вчера",
  "period.7d": "7 дней",
  "period.14d": "14 дней",
  "period.30d": "30 дней",
  "period.mtd": "Этот месяц",
  "period.lastmonth": "Прошлый месяц",
  "period.custom": "Свой",
  "period.compareWith": "Сравнить с",
  "period.cmp.prev": "прошлый период",
  "period.cmp.year": "год назад",
  "period.cmp.off": "без сравнения",
  "period.badge.prev": "к прошлым {days}",
  "period.badge.year": "к году назад",
  "period.against": "против {range}",
  "period.basis": "Конверсии по",
  "period.basis.lead": "дате лида",
  "period.basis.conv": "дате конверсии",
  "period.basis.click": "дате клика",
  "foot.lastClick": "Последний клик {age} назад",
  "foot.noClicks": "Кликов нет двое суток",
  "foot.lastPostback": "Постбек {age} назад",
  "foot.noPostbacks": "Постбеков нет двое суток",
  "foot.unavailable": "Статус недоступен",
  "foot.day": "День по {tz}",
  "foot.currency": "Деньги в USD",
  "settings.tab.alerts": "Оповещения",
  "table.bulk.selected": "Выбрано {count}",
  "table.bulk.clear": "Снять выбор",
  "table.bulk.region": "Массовые действия",
  "table.bulk.cleared": "Выбор снят",
  "table.bulk.ok": "Готово: {ok} из {total}",
  "table.bulk.failed": "Не вышло: {failed} из {total}. {reason}",
  "kpi.botsCut": "боты {pct}",
  "kpi.cr": "CR {cr}",
  "kpi.approve": "{pct} из решённых",
  "kpi.hold": "В холде",
  "kpi.inFlight": "ещё ≈ {sum} в пути",
  "kpi.epc": "EPC {epc}",
  "kpi.cpa": "CPA {cpa}",
  "kpi.was": "было {value}",
  "cols.visits": "Визиты",
  "cols.clicks": "Клики",
  "cols.uniques": "Уники",
  "cols.bots": "Боты",
  "cols.botsPct": "Боты %",
  "cols.lpShows": "LP показы",
  "cols.lpClicks": "LP клики",
  "cols.lpCtr": "LP CTR",
  "cols.toOfferShort": "На оффер",
  "cols.toOffer": "Переходы на оффер",
  "cols.leads": "Лиды",
  "cols.approved": "Апрувы",
  "cols.hold": "Холд",
  "cols.rejected": "Отказ",
  "cols.trash": "Треш",
  "cols.cr": "CR",
  "cols.outcome": "Исход лидов",
  "cols.approveRate": "Апрув",
  "cols.revenue": "Доход",
  "cols.revenueHold": "Доход в холде",
  "cols.cost": "Расход",
  "cols.profit": "Профит",
  "cols.roi": "ROI",
  "cols.epc": "EPC",
  "cols.cpc": "CPC",
  "cols.cpl": "CPL",
  "cols.cpa": "CPA",
  "cols.roas": "ROAS",
  "cols.hint.visits": "все заходы",
  "cols.hint.clicks": "люди",
  "cols.hint.bots": "шт.",
  "cols.hint.lpCtr": "клик по кнопке",
  "cols.hint.cr": "лиды / клики",
  "cols.hint.outcome": "полоска",
  "cols.hint.approveRate": "из решённых",
  "cols.hint.revenue": "апрувы",
  "cols.hint.formula": "формула",
  "filter.add.chip": "+ Фильтр",
  "campaigns.quick.losing": "Сливают",
  "campaigns.quick.noclicks": "Нет кликов",
  "campaigns.quick.hold50": "Холд > 50%",
  "campaigns.badge.active": "Активна",
  "campaigns.badge.paused": "Пауза",
  "campaigns.badge.draft": "Черновик",
  "campaigns.badge.archived": "Архив",

  "foot.clicksWritten": "Клики пишутся, задержка {lag}",
  "foot.withoutClick": "{count} {postbacks} без клика",
  "word.postbacks": "постбек|постбека|постбеков",

  "admin.tab.detectors": "Детекторы",
  "settings.tab.history": "Журнал изменений",
  "journal.tab.postbacks": "Постбеки",
  "journal.live": "в реальном времени",
  "filter.all": "все",
  "word.conversions": "конверсия|конверсии|конверсий",
  "word.problems": "проблема|проблемы|проблем",
  "tc.human": "Человек",
  "tc.verified_bot": "Проверенный бот",
  "tc.meta_crawler": "Краулер Meta",
  "tc.listed_ip": "Адрес из списка ботов",
  "tc.crawler": "Краулер",
  "tc.no_ua": "Без User-Agent",
  "tc.datacenter": "Дата-центр",
  "tc.bot_score": "Бот по скорингу",
  "tc.bot": "Бот (до классов)",
  "cj.col.time": "Время",
  "cj.col.campaign": "Кампания",
  "cj.col.status": "Статус",
  "cj.col.goal": "Цель",
  "cj.col.payout": "Выплата",
  "cj.col.usd": "В USD",
  "cj.col.network": "Партнёрка",
  "cj.col.txid": "ID в сети",
  "cj.col.ad": "Объявление",
  "cj.col.geo": "Гео",
  "cj.status.all": "Все",
  "cj.afterHold": "после холда {days} д",
  "cj.byLatestStatus": "по дате последнего статуса",
  "cj.noCampaign": "без кампании",
  "cj.noCampaign.hint": "Клик этой конверсии трекер не видел — в отчётах по кампаниям её нет",
  "cj.campaignGone": "Кампания недоступна",
  "cj.noRate": "нет курса",
  "cj.empty": "За этот период и с этими фильтрами конверсий нет.",
  "cj.error": "Не удалось загрузить конверсии",
  "clj.col.class": "Класс",
  "clj.empty": "За этот период и с этими фильтрами кликов нет.",
  "clj.botsEmpty": "Ботов за этот период нет.",
  "clj.error": "Не удалось загрузить клики",
  "pb.all": "Все",
  "pb.replayFixed": "Повторить все исправленные",
  "pb.replayFixed.title": "Повторить {count} {postbacks}?",
  "pb.replayFixed.body": "Трекер применит заново постбеки этой страницы, которые сам больше не повторяет: клик так и не нашёлся или запрос был отклонён. Если причину уже исправили — конверсия запишется; если нет — результат будет тем же. Задвоения не будет: одна и та же конверсия второй раз не считается.",
  "pb.replayFixed.confirm": "Повторить",
  "pb.replay.done": "Повтор запущен: {count} {postbacks}",
  "pb.replay.none": "Повторять нечего — эти постбеки уже приняты или обрабатываются",
  "pb.replay.failed": "Не удалось запустить повтор",
  "pb.col.received": "Пришёл",
  "pb.col.network": "Партнёрка",
  "pb.col.click": "Клик",
  "pb.col.amount": "Сумма",
  "pb.col.result": "Результат",
  "pb.col.became": "Что стало",
  "pb.unknownNetwork": "Неизвестная партнёрка",
  "pb.chip.accepted": "Принят",
  "pb.chip.duplicate": "Повтор",
  "pb.chip.pending": "В обработке",
  "pb.chip.no_click": "Нет клика",
  "pb.chip.unknown_status": "Статус не сопоставлен",
  "pb.chip.no_fx": "Нет курса",
  "pb.chip.rejected": "Отклонён",
  "pb.chip.bad_token": "Неверный токен",
  "pb.result.accepted": "Принят",
  "pb.result.duplicate": "Повтор, уже был",
  "pb.result.pending": "В обработке",
  "pb.result.no_click": "Нет клика",
  "pb.result.no_click.waiting": "Нет клика, ждём {age}",
  "pb.result.unknown_status": "Статус не сопоставлен",
  "pb.result.unknown_status.word": "Статус «{word}» не сопоставлен",
  "pb.result.no_fx": "Нет курса",
  "pb.result.no_fx.cur": "Нет курса {cur}",
  "pb.result.rejected": "Отклонён",
  "pb.result.rejected.why": "Отклонён: {why}",
  "pb.result.bad_token": "Неверный токен",
  "pb.became.new": "новый лид, {status}",
  "pb.became.fxBackfilled": "курс появился — выплата в доходе",
  "pb.became.duplicate": "та же конверсия, второй раз не считаем",
  "pb.became.rankKept": "{status} не откатываем в холд",
  "pb.became.noClick": "клик ещё не дошёл или чужой",
  "pb.became.noClickId": "в запросе нет клика",
  "pb.became.unmapped": "не записан — ждёт сопоставления статуса",
  "pb.became.noFx": "выплата не попала в доход",
  "pb.became.badToken": "запрос отклонён, не записан",
  "pb.became.rejected": "не записан — запрос не прочитать",
  "pb.became.pending": "в очереди",
  "pb.became.replaying": "повтор запрошен, в очереди",
  "pb.detail.request": "Параметры запроса, как пришли",
  "pb.detail.noQuery": "без параметров",
  "pb.detail.writtenLabel": "Записано",
  "pb.detail.written": "конверсия «{goal}», статус {status}, {payout} → {usd}",
  "pb.detail.notWritten": "ничего — конверсия не менялась",
  "pb.detail.why": "Почему",
  "pb.detail.same": "Ещё так же",
  "pb.detail.sameText": "{count} {postbacks} {network} со статусом «{word}» за период, на {usd} USD",
  "pb.detail.no_rate": "нет курса {cur}",
  "pb.detail.status_rank_kept": "решение партнёрки не откатывается в холд",
  "pb.detail.same_state": "то же состояние ещё раз",
  "pb.detail.stale_revision": "более старая ревизия, чем записанная",
  "pb.detail.already_applied": "уже применён",
  "pb.detail.click_not_seen_yet": "клика ещё нет — трекер повторит сам",
  "pb.detail.click_missing_in_request": "в запросе нет id клика",
  "pb.detail.foreign_click": "клик другого рабочего пространства",
  "pb.detail.click_has_no_offer_of_network": "у клика нет оффера этой партнёрки",
  "pb.detail.click_has_no_route_to_offer": "клик не вёл на оффер",
  "pb.detail.status_not_mapped": "слово статуса не сопоставлено",
  "pb.detail.invalid_payout": "выплата — не число",
  "pb.detail.invalid_currency": "неизвестная валюта",
  "pb.detail.invalid_payload": "запрос не прочитать",
  "pb.detail.offer_mismatch": "оффер в запросе не совпал с кликом",
  "pb.detail.offer_ambiguous": "оффер не определить однозначно",
  "pb.detail.missing_network": "партнёрка не найдена",
  "pb.detail.txid_owned_by_other_click": "этот ID в сети уже у другого клика",
  "pb.detail.invalid_partner_timestamp": "неверное время партнёрки",
  "pb.detail.invalid_partner_revision": "неверная ревизия партнёрки",
  "pb.detail.unversioned_after_revision": "без ревизии после версионированных",
  "pb.detail.fx_backfilled": "курс дозаполнен",
  "pb.detail.replay_requested": "повтор запрошен",
  "pb.todo.title": "Что сделать",
  "pb.todo.unmapped": "{network} шлёт статус «{word}». Скажите, что он значит, — трекер применит {count} {postbacks} с этим словом.",
  "pb.todo.mapTo": "Что значит статус",
  "pb.todo.map": "Сопоставить и пересчитать {count}",
  "pb.todo.mapped": "«{word}» = {status}; применяем {count}",
  "pb.todo.mapFailed": "Не удалось сопоставить статус",
  "pb.todo.openNetwork": "Открыть партнёрку",
  "pb.todo.noClickWaiting": "Клик мог ещё не дойти — трекер повторит сам. Если клик чужой (трафик не через трекер), постбек останется здесь.",
  "pb.todo.noClickDead": "Трекер перестал ждать клик. Если клик нашёлся или исправили ссылку постбека — повторите.",
  "pb.todo.noFx": "Конверсия записана, но выплата в {cur} не в доходе, пока нет курса. Курс появится — доход дозаполнится сам.",
  "pb.todo.badToken": "Запрос пришёл с неверным токеном или не с того адреса. Проверьте ссылку постбека в партнёрке.",
  "pb.todo.rejected": "Запрос не прочитать (см. «Почему»). Исправьте настройку партнёрки и повторите.",
  "pb.todo.replay": "Повторить",
  "pb.empty": "За этот период постбеков нет.",
  "pb.error": "Не удалось загрузить постбеки",
  "health.title": "Здоровье данных",
  "health.error": "Не удалось проверить здоровье данных",
  "health.p.unmapped": "{network}: статус «{word}» не сопоставлен",
  "health.p.spendNoClicks": "{sum} USD расхода за 7 дней — без визитов в трекере",
  "health.p.spendNoClicks.sub": "{share} всего расхода: объявления ведут в Keitaro (кампании ещё не переключены) или в этот день никого не привели — в ROI кампаний он не попадает",
  "health.p.leads": "Лиды не доставляются в партнёрку: {count}",
  "health.p.leads.sub": "доставка повторяется; отказавшие — в DLQ",
  "health.p.dead": "{count} событий в DLQ",
  "health.p.dead.sub": "попытки исчерпаны — нужен повтор зависших",
  "health.p.stuck": "{count} событий зависли",
  "health.p.stuck.sub": "в журнале дольше 5 минут без клика-причины",
  "health.p.lag": "Отчёты отстают на {age}",
  "health.p.lag.sub": "цифры за последние минуты ещё не в отчётах",
  "health.a.open": "Открыть",
  "health.ok.leads.sub": "{n} за час, отказов нет",
  "health.ok.ratesPending.sub": "первое обновление — в течение часа",
  "health.p.ratesStale": "Курсы валют не обновлялись {age}",
  "health.p.ratesNever": "Курсы валют не загружены больше 48 ч",
  "health.p.ratesStale.sub": "выплаты и расход в валюте считаются по старому курсу или не попадают в доход",
  "health.p.ratesStale.error": "последняя ошибка: {error}",
  "today.title.date": "Сегодня, {date}",
  "today.updated": "обновлено {age} назад",
  "today.byTodaysLeads": "по сегодняшним лидам",
  "today.chart.profit": "Профит за день, накопленный",
  "today.chart.leads": "Лиды за день, накопленные",
  "today.chart.clicks": "Клики за день, накопленные",
  "today.chart.series": "Что на графике",
  "today.chart.nowLabel": "{value} сейчас",
  "today.chart.wasLabel": "вчера к {at}: {value}",
  "today.legend.today": "сегодня",
  "today.legend.yesterday": "вчера",
  "today.now": "Сейчас, {at}",
  "today.vsYesterdaySameTime": "к вчера в это же время",
  "today.inHold": "Ещё в холде",
  "today.attention": "Требует внимания",
  "today.attention.calm": "Всё спокойно.",
  "today.best": "Лучшие сегодня",
  "today.worst": "Худшие сегодня",
  "dimNone.network": "Без партнёрки",
  "dimNone.offer": "Без оффера",
  "dimNone.landing": "Без ленда",
  "dimNone.campaign": "Без кампании",
  "dimNone.source": "Без источника",
  "dimNone.group": "Без группы",
  "dimNone.creative": "Без объявления",
  "dimNone.flow": "Без потока",
  "dimWord.campaign": "кампания|кампании|кампаний",
  "dimWord.creative": "объявление|объявления|объявлений",
  "dimWord.landing": "ленд|ленда|лендов",
  "dimWord.offer": "оффер|оффера|офферов",
  "dimWord.network": "партнёрка|партнёрки|партнёрок",
  "dimWord.source": "источник|источника|источников",
  "dimWord.country": "страна|страны|стран",
  "dimWord.ad_campaign": "кампания FB|кампании FB|кампаний FB",
  "dimWord.ad_set": "адсет|адсета|адсетов",
  "dimWord.placement": "площадка|площадки|площадок",
  "dimWord.group": "группа|группы|групп",
  "dimWord.flow": "поток|потока|потоков",
  "dimWord.node": "узел|узла|узлов",
  "dimWord.step": "шаг|шага|шагов",
  "dimWord.branch": "ветка|ветки|веток",
  "dimWord.region": "регион|региона|регионов",
  "dimWord.city": "город|города|городов",
  "dimWord.language": "язык|языка|языков",
  "dimWord.device": "тип устройства|типа устройства|типов устройства",
  "dimWord.os": "ОС|ОС|ОС",
  "dimWord.browser": "браузер|браузера|браузеров",
  "dimWord.device_model": "модель|модели|моделей",
  "dimWord.isp": "провайдер|провайдера|провайдеров",
  "dimWord.asn": "ASN|ASN|ASN",
  "dimWord.connection": "подключение|подключения|подключений",
  "dimWord.ip_subnet": "подсеть|подсети|подсетей",
  "dimWord.referrer": "реферер|реферера|рефереров",
  "dimWord.hour": "час|часа|часов",
  "dimWord.weekday": "день недели|дня недели|дней недели",
  "dimWord.day": "день|дня|дней",
  "dimWord.week": "неделя|недели|недель",
  "dimWord.month": "месяц|месяца|месяцев",
  "dimWord.goal": "цель|цели|целей",
  "dimWord.status": "статус|статуса|статусов",
  "dimWord.traffic_class": "класс|класса|классов",
  "dimWord.bot_reason": "причина|причины|причин",
  "wd.1": "Пн",
  "wd.2": "Вт",
  "wd.3": "Ср",
  "wd.4": "Чт",
  "wd.5": "Пт",
  "wd.6": "Сб",
  "wd.7": "Вс",
  "status.approved": "Апрув",
  "status.hold": "Холд",
  "status.rejected": "Отказ",
  "status.trash": "Треш",
  "status.refunded": "Возврат",
  "word.levels": "уровень|уровня|уровней",
  "dimgroup.source": "Источник",
  "dimgroup.path": "Путь",
  "dimgroup.geo": "Гео",
  "dimgroup.device": "Устройство",
  "dimgroup.network": "Сеть",
  "dimgroup.time": "Время",
  "dimgroup.conversion": "Конверсия",
  "dimgroup.quality": "Качество",
  // — карточка кампании, Setup, клик и конверсия (Т7) —
  "card.tabs": "Разделы кампании",
  "card.tab.summary": "Сводка",
  "card.tab.report": "Отчёт",
  "card.tab.conversions": "Конверсии",
  "card.tab.clicks": "Клики",
  "card.tab.flow": "Поток",
  "card.tab.links": "Ссылки и постбеки",
  "card.tab.settings": "Настройки",
  "card.tab.history": "История",
  "card.meta.offer": "оффер",
  "card.meta.landers": "ленды {count}",
  "card.meta.buyer": "баер {name}",
  "card.action.linkFor": "Ссылка для {source}",
  "card.action.link": "Ссылка",
  "card.action.edit": "Изменить",
  "card.toast.copied": "Копия «{name}» создана — проверьте её перед включением",
  "card.money.title": "Деньги по дням",
  "card.money.revenue": "Доход",
  "card.money.cost": "Расход",
  "card.money.profit": "Профит",
  "card.money.by": "Шаг",
  "card.money.day": "День",
  "card.money.hour": "Час",
  "card.money.empty": "За период денег нет",
  "card.path.title": "Путь клика",
  "card.path.aside": "люди без ботов",
  "card.path.visits": "Визиты",
  "card.path.visits.note": "все заходы",
  "card.path.bots": "Боты отсеяны",
  "card.path.people": "Люди",
  "card.path.people.note": "= клики",
  "card.path.shows": "Увидели ленд",
  "card.path.cta": "Клик по кнопке",
  "card.path.lead": "Лид",
  "card.path.approve": "Апрув",
  "card.outcome.title": "Исходы лидов",
  "card.outcome.wait": "Сколько ждёт холд",
  "card.outcome.wait.none": "В холде пусто",
  "card.insight.title": "Что выделяется",
  "card.insight.aside": "за период",
  "card.insight.none": "За период ничего не выделяется",
  "card.insight.open": "Раскрыть",
  "card.insight.compare": "Сравнить",
  "card.insight.report": "Отчёт",
  "card.insight.list": "Список",
  "card.insight.expensive_ad.title": "Объявление {ad} дорогое",
  "card.insight.expensive_ad.body": "CPA {cpa} против {campaignCpa} у кампании, {cost} потрачено, профит {profit}",
  "card.insight.weak_lander.title": "{worst} хуже, чем {best}",
  "card.insight.weak_lander.body": "LP CTR {worstCtr}% против {bestCtr}% при сопоставимом трафике",
  "card.insight.night_rejects.title": "Ночные лиды отклоняют",
  "card.insight.night_rejects.body": "{share}% лидов 00–06 ч, из них {rejected}% отказ или треш",
  "card.insight.stale_hold.title": "{count} лидов в холде дольше 3 дней",
  "card.insight.stale_hold.body": "≈ {sum} USD ждут решения {network} — спросить менеджера",
  "card.report.spendWithoutClicks": "Расход без кликов",
  "card.clicks.lander": "Ленд",
  "card.clicks.class": "Класс",
  "word.people": "человек|человека|человек",
  "word.bots": "бот|бота|ботов",
  "card.clicks.empty": "За период визитов нет",
  "card.conv.statusAt": "Статус с",
  "card.conv.ad": "Объявление",
  "card.conv.wait.h": "{n} ч",
  "card.settings.token": "Токен",
  "card.history.system": "система",
  "card.history.empty": "Изменений не записано",
  "card.history.error": "История не загрузилась",
  "card.history.field.status": "статус",
  "card.history.field.branches": "ветки ухода",
  "card.history.field.schedule": "расписание",
  "card.history.field.notes": "заметки",
  "click.meta.at": "{at} по {tz}",
  "click.notFound": "Клик не найден",
  "click.copied": "Скопировано",
  "click.markBot": "Пометить как бот",
  "click.markedBot": "Помечено — оценка учтёт это",
  "click.path": "Путь",
  "click.path.none": "Шагов не записано",
  "click.path.lander": "Ленд",
  "click.path.offer": "оффер",
  "click.path.lead": "Лид",
  "click.conversions.none": "На клике конверсий нет",
  "click.conv.open": "Открыть",
  "click.who": "Кто",
  "click.who.isp": "Сеть",
  "click.who.language": "Язык",
  "click.who.device": "Устройство",
  "click.who.noMatchKeys": "Нет пикселя и fbclid — этот клик не сопоставится в Facebook",
  "conv.title": "Конверсия {id}",
  "conv.meta.payout": "выплата {payout}",
  "conv.meta.goal": "цель «{goal}»",
  "conv.openClick": "Открыть клик",
  "conv.history": "Как менялся статус",
  "conv.history.none": "Событий не записано",
  "conv.step.lead": "Лид",
  "conv.step.first": "партнёрка приняла лид",
  "conv.step.byPostback": "по постбеку",
  "conv.step.after": "через {gap}",
  "conv.raw": "Что прислала партнёрка",
  "conv.raw.none": "Ничего не сохранено",
  "conv.raw.accepted": "Принят {at} · {status}",
  "conv.raw.match": "сопоставлено: {source}",
  "conv.origin": "Откуда лид",
  "conv.origin.click": "Клик",
  "conv.origin.leadAfter": "лид через {gap}",
  "conv.origin.noClick": "клик не найден",
  "conv.origin.ad": "Объявление",
  "conv.origin.campaignAd": "Кампания FB",
  "conv.origin.lander": "Ленд",
  "conv.origin.geo": "Гео",
  "conv.origin.device": "Устройство",
  "gap.s": "{s} с",
  "gap.ms": "{m} мин {s} с",
  "gap.hm": "{h} ч {m} мин",
  "gap.dh": "{d} дн {h} ч",
  "panel.close": "Закрыть",
  "word.days": "день|дня|дней",
  "word.hours": "час|часа|часов",
  "word.minutes": "минуту|минуты|минут",
  "word.seconds": "секунду|секунды|секунд",
  "links.source": "источника",
  "links.checked": "проверено: клик дошёл {ago} назад",
  "links.waiting": "ждём тестовый клик",
  "links.adField": "в поле «{field}»",
  "links.domainNotServing": "{fqdn} ещё не отдаёт — дождитесь привязки перед запуском",
  "links.partners": "Партнёркам",
  "links.test.send": "Отправить тестовый постбек",
  "links.test.needClick": "Сначала откройте тестовый клик — тест уходит только на ваш клик",
  "links.test.sent": "Тестовый постбек: {result}",
  "links.unmapped": "Статус «{status}» от {network} не сопоставлен — такие лиды уходят в «Здоровье», не в холд. Указать, что он значит →",
  "links.recent": "Последние постбеки",
  "links.recent.none": "За период постбеков нет",
  "links.recent.when": "Когда",
  "links.recent.from": "Откуда",
  "links.recent.got": "Пришло",
  "links.recent.did": "Что сделали",
  "flow.edit": "Изменить правила",
  "flow.rules.none": "Правил нет — все идут по долям",
  "flow.branch.back": "Кнопка «Назад»",
  "flow.branch.exit": "Попытка уйти",
  "flow.branch.idle": "Бездействие",
  "flow.branch.scroll": "Долистал до конца",
  "setup.crumbNew": "Новая",
  "setup.name": "Название",
  "setup.copy": "Копировать",
  "setup.copied": "Скопировано",
  "setup.source.title": "Источник",
  "setup.source.platform": "Площадка",
  "setup.source.pixel": "Пиксель",
  "setup.group": "Группа",
  "setup.group.none": "Без группы",
  "setup.tags": "Теги",
  "setup.notes": "Заметки",
  "setup.offers.offer": "Оффер",
  "setup.offers.landers": "Ленды",
  "setup.rules.title": "Правила потока",
  "setup.rules.if": "Если",
  "setup.rules.match.pick": "Выберите условие",
  "setup.rules.match.country": "страна",
  "setup.rules.match.notCountry": "страна не",
  "setup.rules.match.device": "устройство",
  "setup.rules.match.bot": "бот или краулер",
  "setup.rules.match.schedule": "часы",
  "setup.rules.match.paramNot": "в ссылке нет ключа",
  "setup.rules.match.field": "поле визита",
  "setup.rules.device.mobile": "мобильный",
  "setup.rules.device.tablet": "планшет",
  "setup.rules.device.desktop": "десктоп",
  "setup.rules.target.offer": "оффер {name}",
  "setup.rules.target.lander": "ленд {name}",
  "setup.domain.title": "Домен",
  "setup.domain.domain": "Свой свободный домен",
  "setup.domain.https": "HTTPS готов",
  "setup.domain.binding": "ещё привязывается",
  "setup.domain.path": "Путь",
  "setup.cost.title": "Расход",
  "setup.cost.auto": "Из Facebook автоматически",
  "setup.cost.cpc": "За клик",
  "setup.cost.cpa": "За лид",
  "setup.cost.revshare": "Доля дохода",
  "setup.cost.manual": "Вручную за период",
  "setup.cost.rate": "Ставка, USD",
  "setup.postbacks.none": "Выберите оффер — здесь появится постбек его партнёрки",
  "setup.postbacks.last": "последний {ago} назад",
  "setup.postbacks.never": "постбеков за неделю не было",
  "links.capi.idle": "событий нет",
  "links.capi.live": "работает",
  "links.capi.test": "только тест",
  "links.capi.none": "Событий по кампании пока нет — партнёр ещё не присылал решений по её лидам.",
  "links.capi.state.delivered": "передано на отправку",
  "links.capi.state.waiting": "ждёт отправки",
  "links.capi.state.dead": "не доставлено",
  "links.capi.state.test": "тест, в Facebook не ушло",
  // — каталоги больше страницы: выбор, имена, списки —
  "picker.offer.pick": "Выберите оффер",
  "picker.offer.search": "Поиск по всем офферам…",
  "picker.offer.none": "Без оффера",
  "picker.lander.pick": "Выберите ленд",
  "picker.lander.search": "Поиск по всем лендам…",
  "picker.lander.none": "Без ленда",
  "picker.flow.pick": "Выберите поток",
  "picker.flow.search": "Поиск по всем потокам…",
  "picker.flow.none": "Без потока",
  "picker.source.pick": "Выберите источник",
  "picker.source.search": "Поиск источника…",
  "picker.source.none": "Без источника",
  "picker.domain.pick": "Выберите домен",
  "picker.domain.search": "Поиск по всем доменам…",
  "picker.domain.none": "Без домена",
  "picker.campaign.pick": "Выберите кампанию",
  "picker.campaign.search": "Поиск по всем кампаниям…",
  "picker.campaign.none": "Все кампании",
  "picker.keys.move": "по списку",
  "picker.keys.pick": "выбрать",
  "picker.failed": "Не удалось загрузить список",
  "picker.nothing": "Ничего не найдено",
  "picker.more": "ещё {count} — уточните поиск",
  "links.failed": "Не удалось собрать ссылку",
  "links.noSource": "У кампании не выбран источник трафика — ссылку для объявления собрать не из чего.",
  "links.noSource.pick": "Выбрать источник в настройках",
  "links.preview": "Ссылка показана для проверки, а не для вставки: {reason}",
  // — выбор ленда в редакторе —
  "picker.usedIn": "в {flows} потоках · {campaigns} кампаниях",
};
